import { useEffect } from 'react'
import { getToken, onMessage } from 'firebase/messaging'
import { messagingPromise } from '../firebase'
import config from '../config'

const VAPID_KEY = config.vapidKey
const SW_SCOPE = '/firebase-cloud-messaging-push-scope/'
let isTokenRequestInProgress = false
let hasTokenRequestRun = false

const buildSwUrl = () => {
	const {
		apiKey,
		authDomain,
		projectId,
		storageBucket,
		messagingSenderId,
		appId,
		measurementId
	} = config.firebase.web
	const params = new URLSearchParams({
		apiKey,
		authDomain,
		projectId,
		storageBucket,
		messagingSenderId,
		appId
	})
	if (measurementId) params.set('measurementId', measurementId)
	return `/firebase-messaging-sw.js?${params.toString()}`
}

const waitForActive = (registration, timeout = 10000) =>
	new Promise((resolve, reject) => {
		if (registration.active && registration.active.state === 'activated' && registration.pushManager) {
			resolve(registration)
			return
		}

		const worker = registration.installing || registration.waiting

		if (!worker) {
			reject(new Error('Service worker is not ready.'))
			return
		}

		const timeoutId = setTimeout(() => {
			reject(new Error('Service worker activation timed out.'))
		}, timeout)

		worker.addEventListener('statechange', () => {
			if (worker.state === 'activated' && registration.pushManager) {
				clearTimeout(timeoutId)
				resolve(registration)
			}

			if (worker.state === 'redundant') {
				clearTimeout(timeoutId)
				reject(new Error('Service worker became redundant.'))
			}
		})
	})

const getExistingFcmRegistration = async () => {
	const expectedScope = new URL(SW_SCOPE, window.location.origin).href
	const registrations = await navigator.serviceWorker.getRegistrations()
	return registrations.find(registration => registration.scope === expectedScope) || null
}

const useFirebaseNotifications = ({ onForegroundMessage, enabled = true } = {}) => {
	useEffect(() => {
		if (!enabled) return

		const requestToken = async () => {
			if (isTokenRequestInProgress || hasTokenRequestRun) return

			isTokenRequestInProgress = true

			if (
				!('Notification' in window) ||
				!window.isSecureContext ||
				!('serviceWorker' in navigator) ||
				!('PushManager' in window)
			) {
				isTokenRequestInProgress = false
				return
			}

			let permission = Notification.permission
			if (permission === 'default') permission = await Notification.requestPermission()
			if (permission !== 'granted') {
				isTokenRequestInProgress = false
				return
			}

			try {
				const messaging = await messagingPromise
				if (!messaging) {
					isTokenRequestInProgress = false
					return
				}
				const existingReg = await getExistingFcmRegistration()
				const reg =
					existingReg || (await navigator.serviceWorker.register(buildSwUrl(), { scope: SW_SCOPE }))
				const activeReg = await waitForActive(reg)
				const token = await getToken(messaging, {
					vapidKey: VAPID_KEY,
					serviceWorkerRegistration: activeReg
				})
				console.log(token ? `Token: ${token}` : 'No token available.')
				if (token) {
					try {
						localStorage.setItem('fcmToken', token)
					} catch (e) {
						console.warn('Failed to persist FCM token:', e.message)
					}
				}
				hasTokenRequestRun = true
			} catch (err) {
				console.log('FCM setup error:', err)
			} finally {
				isTokenRequestInProgress = false
			}
		}

		const onFirstClick = () => {
			if (Notification.permission === 'default') requestToken()
		}

		requestToken()
		window.addEventListener('click', onFirstClick, { once: true })

		let unsubscribe = () => {}
		messagingPromise.then(messaging => {
			if (!messaging) return
			unsubscribe = onMessage(messaging, payload => {
				console.log('[FCM] Foreground message received:', payload)
				if (typeof onForegroundMessage === 'function') {
					return onForegroundMessage(payload)
				}

				if (payload && payload.notification) {
					const { title, body } = payload.notification

					if (Notification.permission === 'granted') {
						new Notification(title, {
							body: body,
							icon: '/logo192.png' 
						})
					}
				}
			})
		})

		return () => {
			window.removeEventListener('click', onFirstClick)
			unsubscribe()
		}
	}, [enabled, onForegroundMessage])
}

export default useFirebaseNotifications
